// noinspection ES6UnusedImports
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Landmark from '@core/scripts/components/landmark-adapter';
import { Dialog, Typography } from "@bonprix/design-system";
import { useAppBridge } from '@core/scripts/modules/shoppingApp/appBridge.ts';

export default function WishlistErrorLayer({ errorMessage, isWishListErrorLayerOpen, close, texts }) {
    const contentRef = useRef(null);
    const appBridge = useAppBridge();

    const errorMessages = useMemo(
        () => ({
            FULL: {
                headline: texts.bff_wishlist_error_full_headline,
                content: texts.bff_wishlist_error_full_content,
                landmark: 'wishlist_error_layer--full',
            },
            ERROR: {
                headline: texts.bff_wishlist_error_default_headline,
                content: texts.bff_wishlist_error_default_content,
                landmark: 'wishlist_error_layer--default',
            },
            DUPLICATE: {
                headline: texts.bff_wishlist_error_default_headline,
                content: texts.bff_wishlist_error_default_content,
                landmark: 'wishlist_error_layer--duplicate',
            },
            REMOVE: {
                headline: texts.bff_wishlist_error_remove_headline,
                content: texts.bff_wishlist_error_remove_content,
                landmark: 'wishlist_error_layer--remove',
            },
            FALLBACK: {
                headline: texts.bff_wishlist_error_default_headline,
                content: '',
                landmark: 'wishlist_error_layer--default',
            },
        }),
        [texts],
    );

    useEffect(() => {
        if (isWishListErrorLayerOpen) {
            const lmValue = errorMessages[errorMessage]?.landmark || errorMessages.FALLBACK.landmark;
            Landmark.sendLandmark({
                landmark: 'layer_view',
                name: lmValue,
            });
            let toWishlistLinkElement;
            setTimeout(() => {
                // setTimeout to ensure the DOM is fully updated before querying the element
                const toWishlistLinkElement = contentRef.current?.querySelector('a');
                if (toWishlistLinkElement) {
                    toWishlistLinkElement.addEventListener('click', onClickToWishlistLink);
                }
            }, 0);

            return () => {
                if (toWishlistLinkElement) {
                    toWishlistLinkElement.removeEventListener('click', onClickToWishlistLink);
                }
            };
        }
    }, [isWishListErrorLayerOpen]);

    const onClickToWishlistLink = (event) => {
        event.preventDefault();
        const lmValue = errorMessages[errorMessage]?.landmark || errorMessages.FALLBACK.landmark;
        Landmark.sendClickLandmark(lmValue + '_toWishlist');
        window.location.href = event.currentTarget.href;
    };

    const closeErrorLayer = useCallback(() => {
        close();
        const lmValue = errorMessages[errorMessage]?.landmark || errorMessages.FALLBACK.landmark;
        Landmark.sendClickLandmark(lmValue + '_close');
    }, [close, errorMessages, errorMessage]);

    const handleAppResume = useCallback(() => {
        if (!isWishListErrorLayerOpen) return;
        closeErrorLayer();
    }, [isWishListErrorLayerOpen, closeErrorLayer]);
    appBridge.useAppResumeListener(handleAppResume);

    const { headline, content } = errorMessages[errorMessage] || errorMessages.FALLBACK;

    return (
        <Dialog zIndex={99999} isOpen={isWishListErrorLayerOpen} onClose={closeErrorLayer}>
            <Dialog.Title>{headline}</Dialog.Title>
            <Dialog.Content>
                <Typography component="div">
                    <span
                        ref={contentRef}
                        dangerouslySetInnerHTML={{
                            __html: content,
                        }}
                    />
                </Typography>
                <br />
            </Dialog.Content>
            <Dialog.ButtonSection>
                <Dialog.PrimaryButton onClick={closeErrorLayer}>
                    {texts.checkout__wishlist_errorlayer_button_close}
                </Dialog.PrimaryButton>
            </Dialog.ButtonSection>
        </Dialog>
    );
}
